// Derives chart-ready shapes from a report's `summary` distributions and
// `rows`, since report payloads vary per category — this keeps
// ReportPreviewPage generic instead of hand-wiring charts per type.

const DATE_FIELD_CANDIDATES = ["date", "hiringDate", "startDate", "requestedDate", "timestamp"];

export const getDistributions = (summary = {}) =>
  Object.entries(summary)
    .filter(([, v]) => v && typeof v === "object" && !Array.isArray(v))
    .map(([key, value]) => ({
      key,
      data: Object.entries(value).map(([name, val]) => ({
        name,
        value: typeof val === "object" ? (val.count ?? 0) : Number(val) || 0,
      })).filter((d) => d.value > 0),
    }))
    .filter((d) => d.data.length > 0);

export const getSummaryCards = (summary = {}) =>
  Object.entries(summary)
    .filter(([, v]) => typeof v !== "object" || v === null)
    .map(([key, value]) => ({ key, value }));

/** Backend summary keys are named like "statusDistribution" /
 * "departmentDistribution" / "typeDistribution" (see reportService.js —
 * getDistributions() below picks up any object-valued summary field).
 * Strip that "Distribution"/"Breakdown" suffix (and a stray leading "by")
 * to get the bare dimension name before it gets a title built around it —
 * otherwise a title template that already says "Distribution" ends up
 * reading "Status Distribution Distribution". */
export const baseDistributionKey = (key = "") =>
  key.replace(/^by/, "").replace(/(Distribution|Breakdown)$/i, "").trim() || key;

/** Turns a raw summary/distribution key (e.g. "department", "employmentType")
 * into a readable chart title — used so chart headers describe what they
 * actually show instead of a generic "Bar Chart" / "Line Chart" label. */
export const humanizeKey = (key = "") => {
  const stripped = key.replace(/^by/, "");
  const spaced = stripped.replace(/([a-z0-9])([A-Z])/g, "$1 $2").trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const findDateField = (row) => DATE_FIELD_CANDIDATES.find((f) => row[f]);

/** Buckets rows by month (or day if the range is short) into a trend series. */
export const getTrendSeries = (rows = []) => {
  if (!rows.length) return [];
  const field = findDateField(rows[0]);
  if (!field) return [];

  const buckets = {};
  rows.forEach((r) => {
    const raw = r[field];
    if (!raw) return;
    const str = String(raw);
    // formatted values look like "23/07/2026" (en-GB) — parse defensively.
    // Rows with no real date come through as the literal placeholder string
    // "N/A" (see ReportHelpers.safelyExtractValue on the backend), which is
    // truthy and 3 characters long — the old fallback (`.slice(0, 7)`) happily
    // treated it as a bucket key of its own, adding a fake "N/A" point at the
    // end of the trend line (it sorts after every real "YYYY-MM" key).
    const parts = str.split("/");
    let key;
    if (parts.length === 3 && parts.every((p) => /^\d+$/.test(p))) {
      key = `${parts[2]}-${parts[1]}`;
    } else if (/^\d{4}-\d{2}/.test(str)) {
      key = str.slice(0, 7);
    } else {
      return; // not a recognizable date (e.g. "N/A") — skip it
    }
    buckets[key] = (buckets[key] || 0) + 1;
  });

  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, value]) => ({ name, value }));
};

/** Day-of-week x category grid, used for the Shift/Attendance heat map. */
export const getHeatMapData = (rows = [], xField, yField) => {
  if (!rows.length) return { data: [], xLabels: [], yLabels: [] };
  const xLabels = [...new Set(rows.map((r) => r[xField]).filter(Boolean))];
  const yLabels = [...new Set(rows.map((r) => r[yField]).filter(Boolean))].slice(0, 12);
  const counts = {};
  rows.forEach((r) => {
    const x = r[xField], y = r[yField];
    if (!x || !y || !yLabels.includes(y)) return;
    const k = `${x}|${y}`;
    counts[k] = (counts[k] || 0) + 1;
  });
  const data = Object.entries(counts).map(([k, value]) => {
    const [x, y] = k.split("|");
    return { x, y, value };
  });
  return { data, xLabels, yLabels };
};
